import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';
import { AdminCredentials } from '../models/auth.model';
import { getB2cScopes } from './msal.config';
import { FeatureAccessService } from '../feature-access/feature-access.service';

const API_KEY_STORAGE = 'admin_api_key';
const EMAIL_STORAGE = 'admin_email';
const MODE_STORAGE = 'admin_auth_mode';

export type AuthMode = 'apikey' | 'b2c' | null;

function readStoredMode(): AuthMode {
  const raw = localStorage.getItem(MODE_STORAGE);
  return raw === 'apikey' || raw === 'b2c' ? raw : null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly router = inject(Router);
  private readonly msal = inject(MsalService);
  private readonly featureAccess = inject(FeatureAccessService);

  readonly apiKey = signal<string | null>(localStorage.getItem(API_KEY_STORAGE));
  readonly email = signal<string | null>(localStorage.getItem(EMAIL_STORAGE));
  readonly mode = signal<AuthMode>(readStoredMode());

  private interactiveRedirect: Promise<void> | null = null;

  readonly isLoggedIn = computed(() => {
    const m = this.mode();
    if (m === 'apikey') return !!this.apiKey() && !!this.email();
    if (m === 'b2c') return this.msal.instance.getAllAccounts().length > 0;
    return false;
  });

  async login(credentials: AdminCredentials): Promise<void> {
    localStorage.setItem(API_KEY_STORAGE, credentials.apiKey);
    localStorage.setItem(EMAIL_STORAGE, credentials.email);
    this.apiKey.set(credentials.apiKey);
    this.email.set(credentials.email);
    this.setMode('apikey');
    await this.featureAccess.load();
  }

  async completeB2cLogin(): Promise<void> {
    this.setMode('b2c');
    await this.featureAccess.load();
  }

  setMode(mode: AuthMode): void {
    if (mode) {
      localStorage.setItem(MODE_STORAGE, mode);
    } else {
      localStorage.removeItem(MODE_STORAGE);
    }
    this.mode.set(mode);
  }

  private getActiveAccount(): AccountInfo | null {
    const active = this.msal.instance.getActiveAccount();
    if (active) return active;
    const accounts = this.msal.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msal.instance.setActiveAccount(accounts[0]);
      return accounts[0];
    }
    return null;
  }

  async bearerToken(forceRefresh = false): Promise<string | null> {
    if (this.mode() !== 'b2c') return null;
    const account = this.getActiveAccount();
    if (!account) return null;
    const scopes = getB2cScopes();
    try {
      const result: AuthenticationResult = await firstValueFrom(
        this.msal.acquireTokenSilent({ scopes, account, forceRefresh })
      );
      return result.accessToken;
    } catch (err) {
      if (this.needsInteraction(err)) {
        this.startInteractiveRedirect(scopes, account);
      }
      throw err;
    }
  }

  async handleRedirectResponse(): Promise<void> {
    try {
      const result = await firstValueFrom(this.msal.handleRedirectObservable(), { defaultValue: null });
      if (result?.account) {
        this.msal.instance.setActiveAccount(result.account);
      }
    } catch (err) {
      console.error('B2C redirect response could not be processed', err);
    }
  }

  private startInteractiveRedirect(scopes: string[], account: AccountInfo): void {
    if (this.interactiveRedirect) return;
    // msal-browser's rejection path (resetRequestCache) wipes the in-flight redirect's cached request.
    this.interactiveRedirect = firstValueFrom(this.msal.acquireTokenRedirect({ scopes, account }), {
      defaultValue: undefined,
    }).catch(err => {
      console.error('B2C interactive redirect failed to start; signing out', err);
      this.interactiveRedirect = null;
      this.logout();
    });
  }

  private needsInteraction(err: unknown): boolean {
    if (err instanceof InteractionRequiredAuthError) return true;
    return (
      err instanceof BrowserAuthError &&
      err.errorCode === BrowserAuthErrorCodes.blockIframeReload
    );
  }

  logout(): void {
    const m = this.mode();
    this.featureAccess.reset();
    localStorage.removeItem(API_KEY_STORAGE);
    localStorage.removeItem(EMAIL_STORAGE);
    this.apiKey.set(null);
    this.email.set(null);
    this.setMode(null);
    if (m === 'b2c') {
      this.msal.logoutRedirect().subscribe({
        error: err => {
          console.error('B2C logout redirect failed to start', err);
          this.router.navigate(['/login']);
        },
      });
      return;
    }
    this.router.navigate(['/login']);
  }
}
